import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { apiGet } from '../../../utils/apiFetch';
import apiPath from '../../../utils/pathObj';
import dayjs from 'dayjs';

const SubscriptionUpdated = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [upgradeDetails, setUpgradeDetails] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const transactionId = queryParams.get('transactionId');
    
    if (transactionId) {
      // Decode transaction ID if needed
      const decodedTransactionId = atob(transactionId);
      fetchUpdatedSubscription(decodedTransactionId);
    } else {
      setError('No transaction information found');
      setLoading(false);
    }
  }, [location]);

  const fetchUpdatedSubscription = async (transactionId) => {
    try {
      const res = await apiGet(apiPath.getSubscription, {});
      const data = res?.data?.results;
      setSubscriptionData(data);

      // Find the latest upgrade from history
      if (data?.upgradeHistory?.length > 0) {
        // Get the most recent upgrade
        const latestUpgrade = data.upgradeHistory[data.upgradeHistory.length - 1];
        setUpgradeDetails(latestUpgrade);
      }

      setLoading(false);
    } catch (error) {
      console.log("error:", error);
      setError('Failed to fetch subscription details');
      setLoading(false);
    }
  };

  const handleGoToDashboard = () => {
    navigate('/dashboard');
  };

  const handleGoToSubscription = () => {
    navigate('/profile');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
          <p className="mt-4 text-gray-600">Processing your subscription update...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-100 mb-4">
              <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Oops! Something went wrong</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <button
              onClick={handleGoToDashboard}
              className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors duration-200"
            >
              Go to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Calculate total employees
  const totalEmployees = (subscriptionData?.noOfEmployee || 0) + (subscriptionData?.additionalEmployees || 0);
  const totalAmount = (subscriptionData?.subscriptionAmount || 0) + (subscriptionData?.additionalAmountPaid || 0);

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Success Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-green-100 mb-4">
            <svg className="w-10 h-10 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
            </svg>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Payment Successful!</h1>
          <p className="text-gray-600">Your subscription has been updated with the new employee count.</p>
        </div>

        {/* Upgrade Summary - Show this prominently */}
        {upgradeDetails && (
          <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-lg shadow-lg overflow-hidden mb-8 border-2 border-green-200">
            <div className="px-6 py-4 bg-gradient-to-r from-green-500 to-blue-500">
              <h2 className="text-xl font-semibold text-white">🎉 Upgrade Summary</h2>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center p-4 bg-white rounded-lg shadow">
                  <p className="text-sm text-gray-500 mb-1">Additional Employees</p>
                  <p className="text-3xl font-bold text-green-600">+{upgradeDetails.additionalEmployees}</p>
                </div>
                <div className="text-center p-4 bg-white rounded-lg shadow">
                  <p className="text-sm text-gray-500 mb-1">Amount Paid</p>
                  <p className="text-3xl font-bold text-blue-600">${upgradeDetails.amountPaid}</p>
                </div>
                <div className="text-center p-4 bg-white rounded-lg shadow">
                  <p className="text-sm text-gray-500 mb-1">Upgraded On</p>
                  <p className="text-lg font-semibold text-gray-800">
                    {dayjs(upgradeDetails.upgradedAt).format('MMM DD, YYYY')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Subscription Details */}
        {subscriptionData && (
          <div className="bg-white rounded-lg shadow-lg overflow-hidden mb-8">
            <div className="px-6 py-4 bg-blue-600">
              <h2 className="text-xl font-semibold text-white">Current Subscription Details</h2>
            </div>
            <div className="p-6">
              {/* Comparison Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">Original Plan</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Base Employees:</span>
                      <span className="font-semibold">{subscriptionData.noOfEmployee}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Base Price:</span>
                      <span className="font-semibold">${subscriptionData.subscriptionAmount}</span>
                    </div>
                  </div>
                </div>
                <div className="bg-green-50 p-4 rounded-lg">
                  <h3 className="text-sm font-semibold text-green-600 uppercase mb-3">Updated Plan</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Total Employees:</span>
                      <span className="font-semibold text-green-600">{totalEmployees}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Total Amount:</span>
                      <span className="font-semibold text-green-600">${totalAmount}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Detailed Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <p className="text-sm text-gray-500">Subscription Name</p>
                  <p className="text-lg font-semibold text-gray-900">{subscriptionData.subscriptionName}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Price Per Employee</p>
                  <p className="text-lg font-semibold text-gray-900">
                    ${Math.round(subscriptionData.subscriptionAmount / subscriptionData.noOfEmployee)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Number of HRs</p>
                  <p className="text-lg font-semibold text-gray-900">{subscriptionData.noOfHR}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Number of Accountants</p>
                  <p className="text-lg font-semibold text-gray-900">{subscriptionData.noOfAccountant}</p>
                </div>
                {subscriptionData.additionalAmountPaid > 0 && (
                  <div>
                    <p className="text-sm text-gray-500">Total Additional Paid to Date</p>
                    <p className="text-lg font-semibold text-green-600">${subscriptionData.additionalAmountPaid}</p>
                  </div>
                )}
                <div>
                  <p className="text-sm text-gray-500">Expiry Date</p>
                  <p className="text-lg font-semibold text-gray-900">
                    {dayjs(subscriptionData.subscriptionExpiryDate).format('MMM DD, YYYY')}
                  </p>
                </div>
              </div>

              {/* Upgrade History */}
              {subscriptionData?.upgradeHistory?.length > 0 && (
                <div className="mt-6 pt-6 border-t">
                  <h3 className="text-lg font-semibold text-gray-800 mb-3">Upgrade History</h3>
                  <div className="space-y-3">
                    {subscriptionData.upgradeHistory.map((upgrade, index) => (
                      <div key={index} className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                            <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                            </svg>
                          </div>
                          <div>
                            <p className="font-medium">Added {upgrade.additionalEmployees} employee(s)</p>
                            <p className="text-sm text-gray-500">{dayjs(upgrade.upgradedAt).format('MMM DD, YYYY hh:mm A')}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-green-600">${upgrade.amountPaid}</p>
                          <p className="text-xs text-gray-400">TX: {upgrade.transactionId?.slice(-8)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <button
            onClick={handleGoToDashboard}
            className="bg-blue-600 text-white px-8 py-3 rounded-lg hover:bg-blue-700 transition-colors duration-200 font-medium shadow-md hover:shadow-lg"
          >
            Go to Dashboard
          </button>
          <button
            onClick={handleGoToSubscription}
            className="bg-white text-blue-600 border-2 border-blue-600 px-8 py-3 rounded-lg hover:bg-blue-50 transition-colors duration-200 font-medium shadow-md hover:shadow-lg"
          >
            View Profile
          </button>
        </div>

        {/* Email Confirmation Message */}
        <div className="mt-8 text-center">
          <div className="inline-flex items-center space-x-2 bg-blue-50 px-4 py-2 rounded-full">
            <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
            </svg>
            <p className="text-sm text-blue-600">
              A confirmation email has been sent to your registered email address with all the details.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionUpdated;